import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import type { VolumeResponse } from '@xver/apis/v1/kufer/volumes/model';
import { TypeToConfirmModal } from '@xver/components/Atoms';
import { useTranslation } from 'react-i18next';

import { useVolumeMutations } from '../hooks/useVolumeMutations';

interface DeleteVolumeModalProps {
    containerId: string;
    volume: VolumeResponse | null;
    opened: boolean;
    onClose: () => void;
}

/**
 * Deleting a volume reclaims the Ceph image, so the data is gone for good. The typed-name gate
 * exists because this is a per-row action on an otherwise healthy container — far easier to hit
 * by accident than deleting the container itself.
 */
export default function DeleteVolumeModal({
    containerId,
    volume,
    opened,
    onClose,
}: DeleteVolumeModalProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { deleteVolume } = useVolumeMutations(containerId);

    return (
        <TypeToConfirmModal
            opened={opened}
            onClose={onClose}
            title={
                <span className="inline-flex items-center gap-2">
                    <IconAlertTriangle size={20} color="#eb5757" aria-hidden="true" />
                    {t('volumes.deleteConfirmTitle', 'Delete this volume?')}
                </span>
            }
            expected={volume?.name ?? ''}
            inputLabel={t('volumes.deleteTypeNameLabel', 'Volume name')}
            inputDescription={t('volumes.deleteTypeNamePrompt', {
                name: volume?.name ?? '',
                defaultValue: 'Type {{name}} to confirm.',
            })}
            confirmLabel={t('volumes.deleteConfirmLabel', 'Delete volume')}
            confirmIcon={<IconTrash size={16} stroke={1.5} aria-hidden="true" />}
            isSubmitting={deleteVolume.isPending}
            onConfirm={() => {
                if (volume) deleteVolume.mutate(volume.id, { onSuccess: onClose });
            }}
        >
            <p className="text-sm text-fg/78">
                {t('volumes.deleteConfirmBody', {
                    name: volume?.name ?? '',
                    defaultValue:
                        '"{{name}}" and all data on it will be permanently destroyed. This cannot be undone, and the container will be redeployed without it.',
                })}
            </p>
        </TypeToConfirmModal>
    );
}
